import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack } from "@mui/material";
import { useGoalsFulfillmentContext } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentContext";
import { DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { FormDropdown } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  type ProposedSolutionWrapperFormType,
  getProposedSolutionWrapperFormSchema,
} from "Forms/Schemas/GoalsFulfillment/ProposedSolution/proposedSolutionWrapperFormSchema";
import { ContractProposalBaseInvestmentToolType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { InvestmentsNsureCaseStatus } from "./InvestmentsNsureCaseStatus";
import { InvestmentsNsureEvaluationPanel } from "./InvestmentsNsureEvaluationPanel";
import { NsureSelectionContext } from "./InvestmentsNsureSelectionContext";
import { InvestmentsNsureSolutionSelect, SOLUTION_SELECT_HEADER_OFFSET } from "./InvestmentsNsureSolutionSelect";

type Props = {
  readonly onFormTypeChange: (type: ContractProposalBaseInvestmentToolType) => void;
  readonly isEditMode: boolean;
  readonly onClose: () => void;
  readonly onContinue: () => void;
  readonly onDelete?: () => void;
};

export const InvestmentsNsureSelectStep = ({ onFormTypeChange, isEditMode, onContinue, onClose, onDelete }: Props) => {
  const { t } = useResource();
  const { selection, evaluation } = useContext(NsureSelectionContext);
  const { isInvestmentCaseLocked, lockedCaseStatus } = useGoalsFulfillmentContext();

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<ProposedSolutionWrapperFormType>({
    resolver: yupResolver(getProposedSolutionWrapperFormSchema(t)),
    mode: "onChange",
    defaultValues: {
      formType: ContractProposalBaseInvestmentToolType.investment,
    },
  });

  const formTypeOptions = (
    Object.keys(ContractProposalBaseInvestmentToolType) as Array<keyof typeof ContractProposalBaseInvestmentToolType>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[value]),
  }));

  return (
    <>
      <DrawerContent>
        <Flex px={Spacing.XXL} flexDirection="column">
          <form>
            <Stack spacing={Spacing.XXL}>
              <Box>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
                    <FormDropdown
                      name="formType"
                      options={formTypeOptions}
                      control={control}
                      labelEndAdornment={<StyledLabelAdornment />}
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions.Title)}
                      errors={errors}
                      disabled={isEditMode}
                      onChange={(value) => {
                        setValue("formType", value as ContractProposalBaseInvestmentToolType);
                        onFormTypeChange(value as ContractProposalBaseInvestmentToolType);
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </form>
          {isInvestmentCaseLocked ? (
            <Flex py={Spacing.XXL} justifyContent={"center"}>
              <InvestmentsNsureCaseStatus caseStatus={lockedCaseStatus} />
            </Flex>
          ) : (
            <Flex sx={{ flexDirection: { xs: "column", lg: "row" } }} gap={Spacing.XL}>
              <InvestmentsNsureSolutionSelect />
              <InvestmentsNsureEvaluationPanel topOffset={SOLUTION_SELECT_HEADER_OFFSET} />
            </Flex>
          )}
        </Flex>
      </DrawerContent>
      <Flex justifyContent={onDelete ? "space-between" : "flex-end"} px={Spacing.XXL} alignItems={"center"}>
        {onDelete && (
          <Button variant="text" color="error" onClick={onDelete}>
            {t(Resources.Common.Delete)}
          </Button>
        )}
        <HStack spacing={Spacing.XL} py={Spacing.XXL} justifyContent="flex-end">
          <Button onClick={onClose} color="secondary">
            {t(Resources.Common.Close)}
          </Button>
          <Button
            onClick={onContinue}
            disabled={
              isInvestmentCaseLocked ||
              !selection ||
              evaluation.hasWarning ||
              evaluation.isPending ||
              evaluation.isDataStale ||
              evaluation.isError
            }
          >
            {t(Resources.Common.Continue)}
          </Button>
        </HStack>
      </Flex>
    </>
  );
};
